<template>
  <div id="app" @click="bgClick">
    <div class="hero hero-sm bg-primary">
      <div class="hero-body" style="display: flex">
        <div style="flex: 1">
          <h1>Try our <span class="label" @click="reload">coding AI</span></h1>
          <h6>Powered by <em>caplena.com</em></h6>
        </div>
        <div><a href="https://caplena.com"><img id="logo" src="./assets/dark_logo_transparent.svg" /></a></div>
      </div>
    </div>
      <div class="context show-md">
        <div class="cbanner">Context</div>
        <div class="codebook-container">
          <div v-for="(cbDescription, cbKey) in codebookDescriptions"
               :key="cbKey"
               class="card codebook-option"
               @click="selectedCodebook = cbKey" :class="{ 'selected': selectedCodebook === cbKey }">
            <div class="card-header">
              <div class="card-title h5">
                <i class="material-icons">{{ cbDescription.icon }}</i>
                <span class="text">{{ cbDescription.title }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="content inp">
        <div class="columns">
          <div class="column col-8">
            <div class="input-container">
              <input class="form-input"
                     @keypress.enter="process(); showExamples = false"
                     named
                     autocomplete="off"
                     type="text"
                     v-model="currentText"
                     id="answer-text"
                     :class="{ 'shake': invalidText }"
                     :disabled="processing"
                     @click.stop="showExamples = true"
                     ref="input"
                     placeholder="Click here to analyse verbatim">
              <transition name="assistant">
                <div class="input-assistant" v-show="showExamples" @click.stop>
                  <div class="arrow" />
                  <div class="question">{{ codebookDescriptions[selectedCodebook].question }}</div>
                  <div class="instructions">Enter your own text or click one of the examples below.</div>
                  <ul class="examples">
                    <li v-for="(example, exampleIdx) in codebookDescriptions[selectedCodebook].examples"
                        :key="exampleIdx"
                        @click="currentText = example; $nextTick(() => $refs.input.focus())">
                      {{ example }}
                      <div style="flex: 1" />
                      <button class="btn"><i class="material-icons">keyboard_arrow_right</i> Select</button>
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
          </div>
        <div class="column col-4">
          <button id="process-btn"
                  class="btn"
                  @click="process"
                  :disabled="processing"
                  :class="{ loading: processing }">Process</button>
          </div>
        </div>
      </div>

      <div class="content">
        <div class="columns">
          <div class="column col-8 col-md-12">

            <div class="toast toast-warning" style="margin: 15px 0" v-if="processingFailed">
              An issue occurred, maybe your internet connection is unstable? Please try again.
            </div>

            <div id="done">
              <transition-group name="list-done" tag="div" id="done-list">
                <div v-for="answer in answersDone.slice(-MAX_DONE_ITEMS).reverse()"
                     v-bind:key="answer.id"
                     class="done-item bg-secondary">
                  <span class="atext">{{ answer.text }}</span>
                  <div class="acodes">
                    <code-chip v-for="(code, codeIdx) in answer.codes"
                               :key="codeIdx"
                               :category="code.category"
                               :color="$color.getMedium(code.categoryIdx)"
                               :label="code.label"
                               active>
                      {{ code.label }}
                    </code-chip>
                  </div>
                </div>
              </transition-group>
            </div>
          </div>

          <div class="column col-4 hide-md">
            <div v-for="(cbDescription, cbKey) in codebookDescriptions"
                 :key="cbKey"
                 class="card codebook-option"
                 @click="selectedCodebook = cbKey" :class="{ 'selected': selectedCodebook === cbKey }">
              <div class="card-header">
                <div class="card-title h5">
                  <i class="material-icons">{{ cbDescription.icon }}</i>
                  <span class="text">{{ cbDescription.title }}</span></div>
                <div class="card-subtitle text-gray">{{ cbDescription.description }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>

import CodeChip from './components/CodeChip'

import axios from 'axios'
let _ = require('lodash')
const CODEBOOK_TELCO = require('./codebook_telco.json')
const CODEBOOK_ECOMMERCE = require('./codebook_ecommerce.json')
const CODEBOOK_APP = require('./codebook_apps.json')
const TEXT_MIN_LEN = 5

const CODEBOOKS = { telco: CODEBOOK_TELCO, ecommerce: CODEBOOK_ECOMMERCE, app: CODEBOOK_APP }

const THRESHOLD = 0.1

_.each(CODEBOOKS, (cb) => {
  let categories = _(cb).map('category').uniq(cb).value()
  cb.forEach(code => {
    code.categoryIdx = categories.indexOf(code.category)
    code.keywords = []
  })
})

export default {
  name: 'Demo',
  components: {
    'code-chip': CodeChip
  },

  data () {
    return {
      currentAnswerID: 0,

      invalidText: false,
      currentText: '',
      showExamples: false,

      answersDone: [],
      processing: false,
      processingFailed: false,
      selectedCodebook: 'telco',

      codebookDescriptions: {
        telco: {
          title: 'Telecommunications',
          description: 'Codes about network, plans and customer service',
          icon: 'phone',
          question: 'Why are you happy or unhappy with your mobile carrier?',
          examples: [
            'Not so great connection, but fair price',
            'Team not very helpful',
            'I\'ve been with Swisscom for years and always loved their service. The terms just fit well and are not too costly.'

          ]
        },
        ecommerce: {
          title: 'E-Commerce',
          description: 'Codes pricing, shipping, quality and customer service.',
          icon: 'store',
          question: 'What went well or not with your last Amazon order?',
          examples: [
            'Shipment destroyed my new dress',
            'I had one-day delivery, but the package took almost a week to be delivered',
            'Although it took long to get someone from support on the line, they were very helpful'
          ]
        },
        app: {
          title: 'Apps',
          description: 'Codes about user-friendliness, design and features.',
          icon: 'touch_app',
          question: 'What do you enjoy or dislike about the new Joyn streaming app?',
          examples: [
            'Super fast! But kind of brittle',
            'Like the interface',
            'Would be great to have an option to download movies'
          ]
        }
      },

      MAX_DONE_ITEMS: 5
    }
  },

  computed: {
    codeID2code () {
      return _.mapValues(CODEBOOKS, cb => {
        let mapping = {}
        cb.forEach(code => { mapping[code.id] = code })
        return mapping
      })
    },

    cbIdx () {
      let cnt = 0
      let mapping = {}
      _.each(CODEBOOKS, (cb, cbKey) => {
        mapping[cbKey] = cnt++
      })
      return mapping
    }
  },

  methods: {
    reload () {
      window.location.reload(true)
    },

    process () {
      this.processingFailed = false

      if (this.currentText.length < TEXT_MIN_LEN) {
        this.invalidText = true
        setTimeout(() => { this.invalidText = false }, 500)
        return
      }

      this.invalidText = false
      this.processing = true

      let cb = this.selectedCodebook

      // https://api.stage.codit.co/api/generic-inference
      axios.post(`https://api.caplena.com/api/generic-inference`, {
        codebook: CODEBOOKS[cb],
        answers: [{ id: 1, text: this.currentText }],
        threshold: THRESHOLD
      }).then(res => {
        let answer = res.data[0]
        this.answersDone.push({
          text: this.currentText,
          codes: answer.codes.length ? _.map(answer.codes, c => this.codeID2code[cb][c]) : [{
            label: 'Other topic', category: 'Other', id: 999, categoryIdx: 0
          }],
          id: this.currentAnswerID++
        })
        this.processing = false
        this.currentText = ''
      }).catch(err => {
        this.processing = false
        this.processingFailed = true
        console.error(err)
      })
    },

    bgClick () {
      this.showExamples = false
    }
  }
}
</script>

<style lang=scss>

$primary-color: #0e5d88;

@import "spectre.css/src/spectre.scss";

.hero {
  padding-left: 80px;
  padding-right: 80px;
}

.hero-body {
  margin: 0;
  padding: 0!important;
}

#logo {
  margin: -20px -10px -20px 0;
  width: 5rem;
}

.content {
  margin: 20px 80px;
  position: relative;
}

.content.inp {
   position: relative;
}

#answer-text, #process-btn {
  padding-left:30px;
  padding-right:30px;
  margin: 0;
  font-size: larger;
  border-radius: .2rem;
  display: block;
  height: 2.5rem;
  width: 100%;
  margin-bottom: 20px;
}

.done-item {
  margin: 10px 0;
  border-radius: .2rem;
  transition: all 1s;
  padding: 10px 20px;
  display: flex;

  .atext {
    flex: 1;
    align-self: center;
  }

  .acodes {
    max-width: 50%;
    position: relative;
    text-align: right;
    .code-chip {
      text-align: center;
      margin-right: 8px;
      max-width: calc(100% - 8px);
    }
  }
}

.codebook-option {
  margin-top: 10px;
  box-shadow: 0 0.25rem 1rem rgba(48,55,66,.15);
  cursor: pointer;
  transition: all 0.25s;
  background: #EEE!important;

  &:hover, &.selected {
    box-shadow: 0 0.25rem 1rem rgba(48,55,66,.3);
    background: #F5F5F5!important;
  }

  &.selected {
    background: #FFF!important;
    color: $primary-color;
  }

  .card-title {
    display: flex;
    align-items: center;

    .material-icons {
      font-size: 20px;
      margin-right: 6px;
    }
  }
}

.context {
  height: 120px;
  .cbanner {
    position: relative;
    z-index: 5;
    display: flex;
    width: 100%;
    height: 30px;
    text-align: center;
    background: #EEE;
    align-items: center;
    justify-content: center;
    font-size: .6rem;
    text-transform: uppercase;
  }

  .codebook-container {
    margin: 0 80px;
    display: flex;
  }

  .codebook-option {
    flex: 1;
    height: 0%;
    font-size: smaller;
    margin-top: 0;
    border-top: 0;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
    margin-right: 20px;
    align-items: center;
    .card-title {
      font-size: 0.8rem;
    }
    &:last-child { margin-right: 0 }
    &.selected {
      padding-top: 32px;
      background: linear-gradient(180deg, #EEE 0%, #FFF 30%)!important;
      border-top-color: #EEE;
    }
  }
}

.list-done-item {
  transition: all 1s ease-out;
  display: inline-block;
  margin-right: 10px;
}
.list-done-enter, .list-done-leave-to {
  opacity: 0;
}
.list-done-enter {
  transform: translateY(-30px);
}
.list-done-leave-to {
  transform: translateY(30px);
}
.list-done-leave-active {
  /* position: absolute; */
}

@keyframes shake {
  from, to {
    -webkit-transform: translate3d(0, 0, 0);
    transform: translate3d(0, 0, 0);
  }

  10%, 30%, 50%, 70%, 90% {
    -webkit-transform: translate3d(-10px, 0, 0);
    transform: translate3d(-10px, 0, 0);
  }

  20%, 40%, 60%, 80% {
    -webkit-transform: translate3d(10px, 0, 0);
    transform: translate3d(10px, 0, 0);
  }
}

.shake {
  animation-duration: 1s;
  animation-fill-mode: both;
  animation-name: shake;
}

.input-assistant {
  margin-top: -12px;
  z-index: 100;
  position: absolute;
  left: 8px;
  top: 100%;
  right: 8px;
  background: #cedee7;
  border: 1px solid $primary-color;
  border-radius: .2rem;
  box-shadow: 0 0 0 0.1rem rgba(14,93,136, 0.2);
  animation: inputAssistant .2s ease-out;

  .question {
    text-align: center;
    background: $primary-color;
    padding: 8px 16px 0;
    color: #FFF;
    font-weight: bold;
  }

  .instructions {
    text-align: center;
    background: $primary-color;
    padding: 0 16px 8px;
    border-bottom: 1px solid #FFF;
    color: #FFF;
    font-style: italic;
    font-size: smaller;
  }

  .examples {
    margin: 1px;
    list-style: none;
    padding: 0;

    li {
      display: flex;
      align-items: center;
      margin: 10px 8px 14px;
      padding: 4px 8px;
      position: relative;
      cursor: pointer;
      transition: all 0.1s ease-out;
      /* background: rgba(255, 255, 255, 0.5); */
      border-radius: .2rem;

      // .material-icons { color: #5755d9 }

      .btn {
        display: flex;
        background: none;
        align-items: center;
        transition: all 0.05s ease-out;
      }

      &::after {
        content: ' ';
        width: 50px;
        margin: 0 auto;
        border-bottom: 6px dotted $primary-color;
        position: absolute;
        bottom: -10px;
        left: 50%;
        margin-left: -25px;
      }

      &:last-child::after {
        display: none;
      }
    }
  }

  .arrow {
    left: 40px;
    top: -12px;
    position: absolute;
    content: '';
    width:0;
    height: 0;
    border:6px solid transparent;
    border-bottom-color: $primary-color;
  }
}

.assistant-enter-active, .assistant-leave-active {
  transition: all .2s ease-out;
}
.assistant-enter {
  opacity: 0;
  transform: translateY(100px) scale(0.9, 0.9);
}
.assistant-leave-to
/* .slide-fade-leave-active for <2.1.8 */ {
  transform: translateY(100px) scale(0.9, 0.9);
  opacity: 0;
}

.bg-secondary {
  background: rgba(14,93,136, 0.1)!important;
}

.btn:focus, .btn:hover {
  background: rgba(14,93,136, 0.1);
}

.btn.active, .btn:active {
  background: $primary-color;
}

@media only screen and (min-width: 841px) {
  .content.inp .input-container {
    position: relative;
  }

  .input-assistant {
    margin-top: 8px;
    left: 0;
    right: 0;
  }
}

@media only screen and (max-width: 840px) {
  .content.inp .columns {
    position: relative;
  }
}

</style>
